// BSV CHANGES
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  ListItemText,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormHelperText,
} from '@mui/material';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {isAfter, isBefore, startOfToday} from 'date-fns';
import dayjs from 'dayjs';
// import EditIcon from '@app/assets/svg/edit-icon';
import {useDispatch, useSelector} from 'react-redux';
import React, {useEffect, useState} from 'react';
import {routeStateActions} from '@app/router/redux';
import {navbarComponentName} from '@app/router/redux/routeHandler';
import {useStyles} from './styles';
import {strings} from '@app/common/strings';
import {columns} from './data';
import {
  deleteJfwRequest,
  getJfwRequest,
  postJfwRequest,
} from '@app/screens/module-specific-utilities/pages/dcr-jfw/redux';
import {getJfwData} from './redux/selectors';
import {billLibraryStateSelector} from '../bill-library/redux/selectors';
import {fetchDivisionCreator} from '../bill-library/redux/slice';

// Define type
type Employee = {
  id: number;
  employeeCode: string;
  name: string;
  department: string;
  designation: string;
  mappedDivision: number[];
  isActive: boolean;
  startDate?: string;
  endDate?: string;
};

type DivisionOption = {
  value: number;
  label: string;
};

export const DcrJfw = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [employeeData, setEmployeeData] = useState<Employee[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editRow, setEditRow] = useState<Employee | null>(null);
  const [creatingRow, setCreatingRow] = useState<boolean>(false);
  const [newRow, setNewRow] = useState<Employee>({
    id: -1,
    employeeCode: '',
    name: '',
    department: '',
    designation: '',
    mappedDivision: [],
    isActive: true,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deactivateIndex, setDeactivateIndex] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const data = useSelector(getJfwData);
  const divisionOptions = useSelector(billLibraryStateSelector.getDivision());

  useEffect(() => {
    dispatch(getJfwRequest({}));
    dispatch(fetchDivisionCreator());
    dispatch(
      routeStateActions.setNavbarComponentName(navbarComponentName.dcrJfw),
    );
    dispatch(
      routeStateActions.setNavbarComponentHeading(
        <div className={classes.navbarComponentHeading}>
          {strings.ModuleSpecificUtilitie.dcrJfwPage.DcrJfw}
        </div>,
      ),
    );
    return () => {
      dispatch(routeStateActions.setNavbarComponentName(null));
    };
  }, []);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const normalized = data.map(emp => ({
        ...emp,
        mappedDivision: (emp.mappedDivision || []).map((d: any) =>
          typeof d === 'string' ? d : d.label || d.name || '',
        ),
      }));
      setEmployeeData(normalized);
    }
  }, [data]);

  const validateFutureDate = (date: Date | null): string | null => {
    if (!date) return 'Date is required';
    if (isBefore(date, startOfToday())) return 'Date cannot be in the past';
    return null;
  };

  const validateEmployee = (emp: Employee): string | null => {
    if (emp.employeeCode.length > 50)
      return 'Employee ID must be at most 50 characters.';
    if (emp.name.length > 50) return 'Name must be at most 50 characters.';
    if (emp.designation.length > 50)
      return 'designationnation must be at most 50 characters.';
    if (emp.department.length > 50)
      return 'Department must be at most 50 characters.';
    return null;
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditRow({...employeeData[index]});
  };

  const handleEditChange = (field: keyof Employee, value: any) => {
    if (editRow) setEditRow({...editRow, [field]: value});
  };

  const handleEditSave = () => {
    if (editingIndex !== null && editRow) {
      const error = validateEmployee(editRow);
      if (error) {
        alert(error);
        return;
      }

      const updated = [...employeeData];
      updated[editingIndex] = editRow;
      setEmployeeData(updated);
      setEditingIndex(null);
      setEditRow(null);
    }
  };
  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditRow(null);
  };

  const handleChange = (field: keyof Employee, value: any) => {
    setNewRow({...newRow, [field]: value});
  };

  const handleAddClick = () => {
    setCreatingRow(true);
  };

  const handleSave = () => {
    const error = validateEmployee(newRow);
    const dateError = validateFutureDate(startDate);
    if (error || dateError) {
      alert(error || dateError);
      return;
    }

    const divisionIds = newRow.mappedDivision.map(String);
    const payload = {
      name: newRow.name,
      employeeCode: newRow.employeeCode,
      designation: newRow.designation,
      department: newRow.department,
      divisionIds,
      startDate: dayjs(startDate).format('YYYY-MM-DDTHH:mm:ss'),
    };
    dispatch(postJfwRequest(payload));
    setTimeout(() => {
      dispatch(getJfwRequest({}));
      setCreatingRow(false);
      setNewRow({
        id: -1,
        employeeCode: '',
        name: '',
        department: '',
        designation: '',
        mappedDivision: [],
        isActive: true,
      });
      setStartDate(null);
    }, 500);
  };

  const handleCancel = () => {
    setCreatingRow(false);
    setNewRow({
      id: -1,
      employeeCode: '',
      name: '',
      department: '',
      designation: '',
      mappedDivision: [],
      isActive: true,
    });
    setStartDate(null)
  };

  const isAddDisabled = (): boolean => {
    return (
      !newRow.employeeCode.trim() ||
      !newRow.name.trim() ||
      !newRow.department.trim() ||
      !newRow.designation.trim() ||
      newRow.mappedDivision.length === 0
    );
  };

  const isUpdateDisabled = (): boolean => {
    return (
      !editRow?.employeeCode.trim() ||
      !editRow?.name?.trim() ||
      !editRow?.designation?.trim() ||
      !editRow?.department?.trim() ||
      !editRow?.mappedDivision?.length
    );
  };

  const openDeactivateDialog = (index: number) => {
    setDeactivateIndex(index);
    setIsDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setIsDialogOpen(false);
    setDeactivateIndex(null);
    setEndDate(null);
  };

  const handleDialogProceed = () => {
    const dateError = validateFutureDate(endDate);
    if (dateError) {
      setEndDateError(dateError);
      return;
    }

    if (deactivateIndex !== null && endDate) {
      const target = employeeData[deactivateIndex];
      console.log(target, 'TTT');
      dispatch(
        deleteJfwRequest({
          id: target.id,
          endDate: dayjs(endDate).format('YYYY-MM-DDTHH:mm:ss'),
        }),
      );
    }
    setIsDialogOpen(false);
    setDeactivateIndex(null);
    setEndDate(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
          overflowX: 'auto',
        }}>
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.id}
                    className={classes.tableHeadCell}
                    sx={{
                      backgroundColor: '#322b7c', // Blue background
                      color: '#fff', // White text
                      fontWeight: 'bold', // Bold text
                      width: col.width ?? 100,
                      maxWidth: col.width ?? 100,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginTop: 150,
                    }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {employeeData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((emp, idx) => {
                  const rowIndex = page * rowsPerPage + idx;
                  const isEditing = editingIndex === rowIndex;
                  const isRowDisabled =
                    !emp.isActive ||
                    editingIndex !== null ||
                    emp.endDate !== null;
                  return (
                    <TableRow
                      key={idx}
                      sx={{backgroundColor: isEditing ? '#e3f2fd' : undefined}}>
                      {columns.map(col => (
                        <TableCell
                          key={col.id}
                          className={classes.tableBodyCell}
                          sx={{
                            width: col.width,
                            maxWidth: col.width,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {isEditing ? (
                            col.id === 'isActive' ? (
                              <Box display="flex" gap={1}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={handleEditSave}
                                  disabled={isUpdateDisabled()}>
                                  {strings.ModuleSpecificUtilitie.jfw.Update}
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={handleEditCancel}>
                                  {strings.ModuleSpecificUtilitie.jfw.Cancel}
                                </Button>
                              </Box>
                            ) : col.id === 'mappedDivision' ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  multiple
                                  displayEmpty
                                  value={newRow.mappedDivision} // array of `value` numbers
                                  onChange={e =>
                                    handleChange(
                                      'mappedDivision',
                                      e.target.value as unknown as number[],
                                    )
                                  }
                                  renderValue={selected =>
                                    (selected as unknown as number[])
                                      .map(
                                        val =>
                                          divisionOptions.find(
                                            (opt: {value: number}) =>
                                              opt.value === val,
                                          )?.label ?? '',
                                      )
                                      .join(', ') || 'Select'
                                  }>
                                  {divisionOptions.map(
                                    (option: DivisionOption) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}>
                                        <Checkbox
                                          checked={newRow.mappedDivision.includes(
                                            option.value,
                                          )}
                                        />
                                        <ListItemText primary={option.label} />
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                              </FormControl>
                            ) : col.id === 'startDate' ||
                              col.id === 'endDate' ? (
                              emp[col.id] ? (
                                dayjs(emp[col.id] as string)
                                  .format('YYYY-MM-DD')
                                  .split('T')[0]
                              ) : (
                                '-'
                              )
                            ) : (
                              <TextField
                                value={
                                  editRow ? (editRow[col.id] as string) : ''
                                }
                                onChange={e =>
                                  handleEditChange(col.id, e.target.value)
                                }
                                size="small"
                                fullWidth
                              />
                            )
                          ) : col.id === 'isActive' ? (
                            <Box display="flex" gap={1} alignItems="center">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                disabled={isRowDisabled}
                                onClick={() => openDeactivateDialog(rowIndex)}>
                                {strings.ModuleSpecificUtilitie.jfw.deactivate}
                              </Button>
                              {/* {isRowDisabled ? null : (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(rowIndex)}
                                    disabled={isRowDisabled}>
                                    <EditIcon />
                                  </IconButton>
                                )} */}
                            </Box>
                          ) : col.id === 'startDate' || col.id === 'endDate' ? (
                            emp[col.id] ? (
                              dayjs(emp[col.id] as string)
                                .format('YYYY-MM-DD')
                                .split('T')[0]
                            ) : (
                              '-'
                            )
                          ) : Array.isArray(emp[col.id]) ? (
                            <Tooltip
                              title={(emp[col.id] as unknown as string[]).join(
                                ', ',
                              )}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  maxWidth: '100%',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}>
                                {(emp[col.id] as unknown as string[]).join(
                                  ', ',
                                )}
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                col.id === 'mappedDivision' &&
                                Array.isArray(emp[col.id])
                                  ? (emp[col.id] as any[])
                                      .map(item =>
                                        typeof item === 'string'
                                          ? item
                                          : item.label || '',
                                      )
                                      .join(', ')
                                  : String(emp[col.id] ?? '')
                              }>
                              <span>
                                {col.id === 'mappedDivision' &&
                                Array.isArray(emp[col.id])
                                  ? (emp[col.id] as any[])
                                      .map(item =>
                                        typeof item === 'string'
                                          ? item
                                          : item.label || '',
                                      )
                                      .join(', ')
                                  : String(emp[col.id] ?? '')}
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {creatingRow && (
        <TableRow sx={{backgroundColor: '#e3f2fd', height: 48}}>
          {' '}
          {/* Light green */}
          {columns.map(col => (
            <TableCell
              key={col.id}
              className={classes.tableBodyCell}
              sx={{
                width: col.width,
                maxWidth: col.width,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {col.id === 'isActive' ? (
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleSave}
                    disabled={isAddDisabled()}>
                    {strings.ModuleSpecificUtilitie.jfw.add}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleCancel}
                    sx={{backgroundColor: '#ffebee'}}>
                    {' '}
                    {strings.ModuleSpecificUtilitie.jfw.delete}
                  </Button>
                </Box>
              ) : col.id === 'mappedDivision' ? (
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    displayEmpty
                    value={newRow.mappedDivision}
                    onChange={e =>
                      handleChange('mappedDivision', e.target.value as number[])
                    }
                    renderValue={selected =>
                      (selected as number[])
                        .map(
                          val =>
                            divisionOptions.find(
                              (opt: {value: number}) => opt.value === val,
                            )?.label ?? '',
                        )
                        .join(', ') || 'Select'
                    }>
                    {divisionOptions.map((option: DivisionOption) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox
                          checked={newRow.mappedDivision.includes(option.value)}
                        />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : col.id === 'endDate' ? (
                '-'
              ) : col.id === 'startDate' ? (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={startDate}
                    label="Select"
                    disablePast
                    onChange={newValue => {
                      setStartDate(newValue);
                      setStartDateError(validateFutureDate(newValue));
                    }}
                    renderInput={params => (
                      <TextField {...params} size="small" />
                    )}
                  />
                  {startDateError && (
                    <FormHelperText error>{startDateError}</FormHelperText>
                  )}
                </LocalizationProvider>
              ) : (
                <TextField
                  value={(newRow[col.id] as string) || ''}
                  onChange={e => handleChange(col.id, e.target.value)}
                  size="small"
                  fullWidth
                />
              )}
            </TableCell>
          ))}
        </TableRow>
      )}
      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        {!creatingRow && editingIndex === null && (
          <Button variant="contained" color="primary" onClick={handleAddClick}>
            {strings.ModuleSpecificUtilitie.jfw.add}
          </Button>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 50, 100]}
          component="div"
          count={employeeData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          sx={{
            borderTop: '1px solid #e0e0e0',
            '& .MuiTablePagination-toolbar': {justifyContent: 'flex-end'},
          }}
        />
      </Box>
      <Dialog open={isDialogOpen} onClose={handleDialogCancel}>
        <DialogTitle>
          {strings.ModuleSpecificUtilitie.jfw.confirmDeactivate}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {strings.ModuleSpecificUtilitie.jfw.askDeactivate}
          </Typography>
          <Box mt={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                disablePast
                // inputFormat="dd-MM-yyyy"
                onChange={newValue => {
                  setEndDate(newValue);
                  setEndDateError(validateFutureDate(newValue));
                }}
                renderInput={params => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
            {endDateError && (
              <FormHelperText error>{endDateError}</FormHelperText>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} color="error">
            {strings.ModuleSpecificUtilitie.jfw.Cancel}
          </Button>
          <Button
            onClick={handleDialogProceed}
            color="primary"
            variant="contained">
            {strings.ModuleSpecificUtilitie.jfw.Proceed}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
